import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, DollarSign, Calendar, Users, Zap } from "lucide-react";
import AffiliateApplyForm from "@/components/affiliate/AffiliateApplyForm";
import { AFFILIATE_TERMS } from "@/lib/affiliate/programTerms";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function AffiliatesPage() {
  const [submitted, setSubmitted] = useState(false);

  usePageMeta({
    fullTitle: true,
    title: "Affiliate Program | LaneRunner",
    description:
      "Refer brokers, carriers, and shippers to LaneRunner and earn recurring commission. Approval-gated program, now accepting applications.",
  });

  return (
    <div className="min-h-screen bg-[#0a0d12] text-white">
      {/* Top bar */}
      <header className="border-b border-[#1e2530] px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight">
            LANE<span className="text-[#1ab7aa]">RUNNER</span>
          </Link>
          <Link to="/" className="text-sm text-[#9ca3af] hover:text-white transition-colors">
            ← Back to site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 sm:py-24 border-b border-[#1e2530]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 text-xs font-medium uppercase tracking-wider rounded-full bg-[#1ab7aa]/10 text-[#1ab7aa] border border-[#1ab7aa]/30 mb-6">
            Approval-gated · Now accepting applications
          </span>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight mb-6">
            Earn <span className="text-[#1ab7aa]">{AFFILIATE_TERMS.commissionPct}%</span> recurring
            <br />
            for every Lanerunner subscriber you send.
          </h1>
          <p className="text-lg sm:text-xl text-[#9ca3af] max-w-2xl mx-auto mb-10">
            Trucking creators, dispatch coaches, brokers, and freight community leaders — earn real
            money introducing your audience to Lanerunner.
          </p>
          <a
            href="#apply"
            className="inline-block px-8 py-4 rounded-lg bg-[#1ab7aa] text-black font-semibold hover:bg-[#15a299] transition-colors"
          >
            Apply now
          </a>
        </div>
      </section>

      {/* Terms grid */}
      <section className="px-6 py-16 border-b border-[#1e2530]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold mb-10 text-center">Program terms at a glance</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <TermCard icon={DollarSign} title={`${AFFILIATE_TERMS.commissionPct}% commission`} body="On every paid subscription, recurring." />
            <TermCard icon={Calendar} title={`${AFFILIATE_TERMS.commissionDurationMonths} months recurring`} body="You earn each month your referral pays." />
            <TermCard icon={Users} title={`${AFFILIATE_TERMS.cookieWindowDays}-day cookie window`} body="Get credit even if they sign up later." />
            <TermCard icon={Zap} title={`$${AFFILIATE_TERMS.minimumPayoutUSD} minimum payout`} body={`${AFFILIATE_TERMS.payoutCadence}, via ${AFFILIATE_TERMS.payoutMethods}.`} />
          </div>
          <p className="text-xs text-[#6b7280] text-center mt-6">
            Subscriptions refunded within {AFFILIATE_TERMS.refundClawbackDays} days are deducted from your balance.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 py-16 border-b border-[#1e2530]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-10 text-center">How it works</h2>
          <ol className="space-y-6">
            <Step n={1} title="Apply">Tell us about your audience. We review every application by hand.</Step>
            <Step n={2} title="Get approved">Approved affiliates receive a personalized referral code and link by email.</Step>
            <Step n={3} title="Share">Drop your link in videos, posts, newsletters, podcasts, or your trucking school.</Step>
            <Step n={4} title="Earn">{AFFILIATE_TERMS.commissionPct}% of every paid month, for 12 months, paid out monthly.</Step>
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-16 border-b border-[#1e2530]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center">Frequently asked</h2>
          <div className="space-y-6">
            <Faq q="Who can apply?">
              Anyone with an engaged audience in trucking, freight, dispatch, or logistics — creators, coaches,
              brokers, school operators, podcast hosts, and community builders. Approval is at our discretion.
            </Faq>
            <Faq q="When am I paid?">
              Payouts are processed monthly via PayPal or ACH once your balance crosses $50. Below that, your
              balance rolls over.
            </Faq>
            <Faq q="How are refunds handled?">
              If a customer cancels and is refunded within {AFFILIATE_TERMS.refundClawbackDays} days, the
              commission for that period is reversed. Anything past the clawback window is yours.
            </Faq>
            <Faq q="Can I run paid ads on your brand?">
              No bidding on Lanerunner-branded keywords or trademarks. Organic and audience-led promotion only.
              We reserve the right to terminate accounts that abuse the program.
            </Faq>
          </div>
        </div>
      </section>

      {/* Apply */}
      <section id="apply" className="px-6 py-16 border-b border-[#1e2530] bg-[#0d1117]">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-2 text-center">Apply to the program</h2>
          <p className="text-sm text-[#9ca3af] mb-8 text-center">
            We review every application personally. Expect to hear back within a few business days.
          </p>
          {submitted ? (
            <div className="rounded-lg border border-[#1ab7aa]/40 bg-[#1ab7aa]/5 p-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-[#1ab7aa] mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Application received</h3>
              <p className="text-sm text-[#9ca3af]">
                Thanks — we'll review your application and reply to the email you provided.
              </p>
            </div>
          ) : (
            <AffiliateApplyForm onSuccess={() => setSubmitted(true)} />
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 text-center text-xs text-[#6b7280]">
        <Link to="/" className="hover:text-white transition-colors">Home</Link>
        <span className="mx-2">·</span>
        <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
        <span className="mx-2">·</span>
        <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
      </footer>
    </div>
  );
}

function TermCard({ icon: Icon, title, body }: { icon: React.ComponentType<{ className?: string }>; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-[#1e2530] bg-[#0d1117] p-5">
      <Icon className="w-5 h-5 text-[#1ab7aa] mb-3" />
      <div className="font-semibold mb-1">{title}</div>
      <div className="text-sm text-[#9ca3af]">{body}</div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1ab7aa] text-black font-bold flex items-center justify-center">
        {n}
      </div>
      <div>
        <div className="font-semibold mb-1">{title}</div>
        <div className="text-sm text-[#9ca3af]">{children}</div>
      </div>
    </li>
  );
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-semibold mb-1">{q}</div>
      <div className="text-sm text-[#9ca3af]">{children}</div>
    </div>
  );
}
